import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
// Base server URL without /api — used for resolving uploaded file paths
export const SERVER_URL = API_URL.replace(/\/api$/, '');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login:          (credentials) => api.post('/auth/login', credentials),
  register:       (data)        => api.post('/auth/register', data),
  forgotPassword: (email)       => api.post('/auth/forgot-password', { email }),
  resetPassword:  (data)        => api.post('/auth/reset-password', data),
  getMe:          ()            => api.get('/auth/me'),
  // Superadmin staff management
  createStaff: (data) => api.post('/auth/staff', data),
  getStaff:    ()     => api.get('/auth/staff'),
  deleteStaff: (id)   => api.delete(`/auth/staff/${id}`),
};

export const workoutPlansAPI = {
  getAll: () => api.get('/plans/workout/all'),
  getOne: (id) => api.get(`/plans/workout/${id}`),
  create: (data) => api.post('/plans/workout', data),
  update: (id, data) => api.put(`/plans/workout/${id}`, data),
  delete: (id) => api.delete(`/plans/workout/${id}`),
  publish: (id, isPublished) => api.put(`/plans/workout/${id}`, { isPublished }),
  assign: (data) => api.post('/plans/admin/assign', data),
};

export const mealPlansAPI = {
  getAll: () => api.get('/plans/meal/all'),
  getOne: (id) => api.get(`/plans/meal/${id}`),
  create: (data) => api.post('/plans/meal', data),
  update: (id, data) => api.put(`/plans/meal/${id}`, data),
  delete: (id) => api.delete(`/plans/meal/${id}`),
  publish: (id, isPublished) => api.put(`/plans/meal/${id}`, { isPublished }),
  generateAI: (data) => api.post('/plans/meal/ai-generate', data),
  downloadTemplate: () => api.get('/plans/meal/template', { responseType: 'blob' }),
  importExcel: (formData) => api.post('/plans/meal/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  assign: (data) => api.post('/plans/admin/assign', data),
};

export const weeklySchedulesAPI = {
  getForPlan: (mealPlanId) => api.get(`/weekly-schedules/plan/${mealPlanId}`),
  create: (data) => api.post('/weekly-schedules', data),
  update: (id, data) => api.put(`/weekly-schedules/${id}`, data),
  delete: (id) => api.delete(`/weekly-schedules/${id}`),
};

export const foodAPI = {
  getAll: (params) => api.get('/food', { params }),
  create: (data) => api.post('/food', data),
  update: (id, data) => api.put(`/food/${id}`, data),
};

export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getOne: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/auth/register', data),
  createBatch: (data) => api.post('/users/batch', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  assignToGym: (data) => api.post('/users/assign-gym', data),
  delete: (id) => api.delete(`/users/${id}`),
};

export const batchesAPI = {
  getAll: (params) => api.get('/batches', { params }),
  create: (data) => api.post('/batches', data),
  update: (id, data) => api.put(`/batches/${id}`, data),
};

export const gymsAPI = {
  getAll: () => api.get('/gyms'),
  getOne: (id) => api.get(`/gyms/${id}`),
  create: (data) => api.post('/gyms', data),
  update: (id, data) => api.put(`/gyms/${id}`, data),
  updatePricing: (id, pricing) => api.put(`/gyms/${id}/pricing`, pricing),
  getNotificationTemplates: (id) => api.get(`/gyms/${id}/notification-templates`),
  saveNotificationTemplates: (id, templates) => api.put(`/gyms/${id}/notification-templates`, { templates }),
  getNotificationSettings: (id) => api.get(`/gyms/${id}/notification-settings`),
  saveNotificationSettings: (id, settings) => api.put(`/gyms/${id}/notification-settings`, { notificationSettings: settings }),
  getPauseSettings: (id) => api.get(`/gyms/${id}/pause-settings`),
  savePauseSettings: (id, settings) => api.put(`/gyms/${id}/pause-settings`, settings),
  getMessageQuota: (id) => api.get(id ? `/gyms/${id}/message-quota` : '/gyms/message-quota'),
  topUpQuota: (data) => api.post('/gyms/topup-message-quota', data),
  createTopUpOrder: (data) => api.post('/gyms/create-topup-order', data),
  verifyTopUpPayment: (data) => api.post('/gyms/verify-topup-payment', data),
  addAdmin: (data) => api.post('/gyms/add-admin', data),
  removeAdmin: (data) => api.post('/gyms/remove-admin', data),
  delete: (id, options) => api.delete(`/gyms/${id}`, { data: options }),
  getPublic: (params) => api.get('/gyms/public', { params }),
  copyContent: (id, data) => api.post(`/gyms/${id}/copy-content`, data),
  getQrCode: (id) => api.get(`/gyms/${id}/qr-code`, { responseType: 'blob' }),
};

export const attendanceAPI = {
  getRecords: (params) => api.get('/attendance', { params }),
  qrCheckIn: (token) => api.post('/attendance/qr-checkin', { token }),
};

export const trainersAPI = {
  // Legacy staff-trainer routes (gym admin management)
  createStaff:     (data) => api.post('/auth/trainer', data),
  getAllStaff:      ()     => api.get('/auth/trainers'),
  deactivateStaff: (id)   => api.delete(`/auth/trainer/${id}`),
  // Expert trainer routes (/api/trainers)
  getAll:          (params) => api.get('/trainers', { params }),
  getOne:          (id)     => api.get(`/trainers/${id}`),
  create:          (data)   => api.post('/trainers', data),
  update:          (id, data) => api.put(`/trainers/${id}`, data),
  deactivate:      (id)     => api.delete(`/trainers/${id}`),
  getQueries:      (params) => api.get('/trainers/queries/my-queries', { params }),
  getAllQueries:    (params) => api.get('/trainers/queries/all', { params }),
  getQuery:        (id)     => api.get(`/trainers/queries/${id}`),
  answerQuery:     (id, data) => api.post(`/trainers/queries/${id}/answer`, data),
};

export const stressContentAPI = {
  getAll:  (params) => api.get('/mental-health/resources/admin', { params }),
  create:  (data)   => api.post('/mental-health/resources/admin', data),
  importStarter: () => api.post('/mental-health/resources/admin/import-starter'),
  update:  (id, data) => api.put(`/mental-health/resources/admin/${id}`, data),
  delete:  (id)     => api.delete(`/mental-health/resources/admin/${id}`),
};

export const yogaContentAPI = {
  getAll:  (params) => api.get('/yoga-content/admin', { params }),
  create:  (data)   => api.post('/yoga-content/admin', data),
  importStarter: () => api.post('/yoga-content/admin/import-starter'),
  update:  (id, data) => api.put(`/yoga-content/admin/${id}`, data),
  delete:  (id)     => api.delete(`/yoga-content/admin/${id}`),
};

export const membersAPI = {
  addToGym:      (data) => api.post('/users/assign-gym', data),
  removeFromGym: (data) => api.post('/users/remove-member', data),
  assignTrainer: (data) => api.post('/users/assign-trainer', data),
};

export const exercisesAPI = {
  getAll: (params) => api.get('/exercises', { params }),
  getOne: (id) => api.get(`/exercises/${id}`),
  generateAI: (data) => api.post('/exercises/ai-generate', data),
  create: (data) => api.post('/exercises', data),
  update: (id, data) => api.put(`/exercises/${id}`, data),
  delete: (id) => api.delete(`/exercises/${id}`),
};

export const workoutsAPI = {
  getAll: (params) => api.get('/workouts', { params }),
  create: (data) => api.post('/workouts', data),
  assign: (data) => api.post('/workouts/assign', data),
  getLogs: (params) => api.get('/workouts/logs', { params }),
  getStats: (params) => api.get('/workouts/stats', { params }),
};

export const trainerPlansAPI = {
  getAll: (params) => api.get('/trainer-plans', { params }),
  getOne: (id) => api.get(`/trainer-plans/${id}`),
  create: (data) => api.post('/trainer-plans', data),
  update: (id, data) => api.put(`/trainer-plans/${id}`, data),
  delete: (id) => api.delete(`/trainer-plans/${id}`),
  publish: (id) => api.put(`/trainer-plans/${id}/publish`),
  unpublish: (id) => api.put(`/trainer-plans/${id}/unpublish`),
};

export const marketingAPI = {
  getTemplates:           ()             => api.get('/marketing/templates'),
  updateTemplate:         (key, ch = 'all', data) => {
    if (typeof ch === 'object' && data === undefined) {
      data = ch;
      ch = 'all';
    }
    return api.put(`/marketing/templates/${key}/${ch}`, data);
  },
  resetTemplate:          (key, ch = 'all') => api.delete(`/marketing/templates/${key}/${ch}`),
  sendMessage:            (data)         => api.post('/marketing/send', data),
  sendWhatsAppBroadcast:  (data)         => api.post('/marketing/broadcast/whatsapp', data),
  getLogs:                (params)       => api.get('/marketing/logs', { params }),
};

export const bannersAPI = {
  getAll: (params) => api.get('/banners', { params }),
  create: (data) => api.post('/banners', data),
  update: (id, data) => api.put(`/banners/${id}`, data),
  delete: (id) => api.delete(`/banners/${id}`),
};

export const goalsAPI = {
  getAll: (params) => api.get('/goals', { params }),
  create: (data) => api.post('/goals', data),
  update: (id, data) => api.put(`/goals/${id}`, data),
  delete: (id) => api.delete(`/goals/${id}`),
};

export const notificationsAPI = {
  send: (data) => api.post('/notifications', data),
  sendBulk: (data) => api.post('/notifications/bulk', data),
  getMy: (params) => api.get('/notifications/my', { params }),
  getSent: (params) => api.get('/notifications/sent', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  getEvents: (params) => api.get('/notifications/events/list', { params }),
  getEventsSummary: () => api.get('/notifications/events/summary'),
  markAllEventsRead: () => api.put('/notifications/events/mark-all-read'),
};

export const invoicesAPI = {
  create: (data) => api.post('/invoices', data),
  sendBulk: (data) => api.post('/invoices/bulk', data),
  getAdminList: (params) => api.get('/invoices/admin', { params }),
  getStats: () => api.get('/invoices/admin/stats'),
  getOne: (id) => api.get(`/invoices/${id}`),
  cancel: (id) => api.patch(`/invoices/${id}/cancel`),
};

export const gymInvoicesAPI = {
  getAll: (params) => api.get('/gym-invoices', { params }),
  getStats: () => api.get('/gym-invoices/stats'),
  create: (data) => api.post('/gym-invoices', data),
  markPaid: (id) => api.patch(`/gym-invoices/${id}/paid`),
  cancel: (id) => api.patch(`/gym-invoices/${id}/cancel`),
};

export const analyticsAPI = {
  getUserStats: (userId) => api.get(`/progress/admin/user/${userId}`),
  getUserMealLogs: (userId, days = 30) => api.get(`/progress/admin/user/${userId}/meals`, { params: { days } }),
  getUserMeasurements: (userId) => api.get(`/measurements/admin/user/${userId}`),
  addUserMeasurement: (userId, data) => api.post(`/measurements/admin/user/${userId}`, data),
  deleteUserMeasurement: (id) => api.delete(`/measurements/admin/${id}`),
};

export const uploadAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadExerciseVideos: ({ maleVideo, femaleVideo, gymName, muscleGroup }) => {
    const formData = new FormData();
    if (maleVideo)   formData.append('maleVideo',   maleVideo);
    if (femaleVideo) formData.append('femaleVideo',  femaleVideo);
    if (gymName)     formData.append('gymName',      gymName);
    if (muscleGroup) formData.append('muscleGroup',  muscleGroup);
    return api.post('/upload/exercise-videos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Delete by full URL (Firebase Storage) or by filename+type (legacy local disk)
  delete: (filename, type) => api.delete('/upload', { data: { filename, type } }),
  deleteByUrl: (url) => api.delete('/upload', { data: { url } }),
  getFileUrl: (type, filename) => `${SERVER_URL}/uploads/${type}/${filename}`,
};

export const superadminAPI = {
  getStats:          ()              => api.get('/superadmin/stats'),
  getFranchiseStats: ()              => api.get('/superadmin/franchise-stats'),
  resetData:         (collections)  => api.post('/superadmin/reset', { collections }),
  deleteUsersByGym:  (gymId)        => api.post('/superadmin/delete-users-by-gym', { gymId }),
  seedData:          (dataTypes)    => api.post('/superadmin/seed', { dataTypes }),
  importIndianFoods: ()             => api.post('/superadmin/import-indian-foods'),
  importMealPlans30: ()             => api.post('/superadmin/import-30-day-meal-plans'),
  runMealRetention:  ()             => api.post('/superadmin/meal-log-retention'),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getAdvancedStats: () => api.get('/dashboard/advanced-stats'),
  getTopActiveUsers: (params) => api.get('/dashboard/top-active-users', { params }),
  getToday: () => api.get('/dashboard/today'),
};

export const feedbackAPI = {
  getAll:  (params) => api.get('/feedback', { params }),
  update:  (id, data) => api.patch(`/feedback/${id}`, data),
  delete:  (id) => api.delete(`/feedback/${id}`),
  submitOnboarding: (data) => api.post('/feedback/onboarding', data),
  submitPublicFeedback: (data) => api.post('/feedback/onboarding', data),
};

export const premiumAPI = {
  getStatus: () => api.get('/payments/status'),
  getTransactions: (params) => api.get('/payments/transactions', { params }),
  getTransactionStats: () => api.get('/payments/transactions/stats'),
  cancelSubscription: (data) => api.post('/payments/cancel-subscription', data),
  getCancellations: (params) => api.get('/payments/cancellations', { params }),
  processRefund: (id, data) => api.patch(`/payments/cancellations/${id}/refund`, data),
};

export const aiPlansAPI = {
  getAllPlans:    (params)  => api.get('/ai-plans/admin/all', { params }),
  getUserPlans:  (userId)  => api.get(`/ai-plans/admin/user/${userId}`),
};

export const aiMealPlansAPI = {
  getUserMealPlans: (userId) => api.get(`/ai-meal-plans/admin/user/${userId}`),
};

export const auditAPI = {
  list: (params) => api.get('/audit', { params }),
  export: (params) => api.get('/audit/export', { params, responseType: 'blob' }),
};

export const exerciseSyncAPI = {
  sync: () => api.post('/sync-exercises'),
};

export const challengesAPI = {
  getAll:       (params) => api.get('/challenges', { params }),
  getOne:       (id)     => api.get(`/challenges/${id}`),
  create:       (data)   => api.post('/challenges', data),
  update:       (id, data) => api.put(`/challenges/${id}`, data),
  delete:       (id)     => api.delete(`/challenges/${id}`),
  getLeaderboard: (id, params) => api.get(`/challenges/${id}/leaderboard`, { params }),
  getAdminStats:  (params) => api.get('/challenges/admin/stats', { params }),
};

export const leadsAPI = {
  getAll:    (params) => api.get('/leads', { params }),
  getStats:  (params) => api.get('/leads/stats', { params }),
  getOne:    (id)     => api.get(`/leads/${id}`),
  create:    (data)   => api.post('/leads', data),
  update:    (id, data) => api.put(`/leads/${id}`, data),
  delete:    (id)     => api.delete(`/leads/${id}`),
  addFollowUp: (id, data) => api.post(`/leads/${id}/follow-up`, data),
  convert:   (id, data) => api.post(`/leads/${id}/convert`, data),
  markLost:  (id, reason) => api.post(`/leads/${id}/mark-lost`, { reason }),
  export:    (params) => api.get('/leads/export', { params, responseType: 'blob' }),
};

export const metaAPI = {
  getConfig:     (params) => api.get('/meta/config', { params }),
  saveConfig:    (data)   => api.put('/meta/config', data),
  getForms:      (params) => api.get('/meta/forms', { params }),
  getAdAccounts: (params) => api.get('/meta/ad-accounts', { params }),
  sync:          (data)   => api.post('/meta/sync', data),
  createCampaign:(data)   => api.post('/meta/campaign', data),
  subscribe:     ()       => api.post('/meta/subscribe'),
};

export const kycAPI = {
  getForUser: (userId) => api.get('/kyc', { params: { userId } }),
  upload:     (formData) => api.post('/kyc/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  verify:     (id)       => api.patch(`/kyc/${id}/verify`),
  reject:     (id, reason) => api.patch(`/kyc/${id}/reject`, { reason }),
  delete:     (id)       => api.delete(`/kyc/${id}`),
  // Member-facing
  getMyDocs:  ()         => api.get('/kyc/my-docs'),
  memberUpload: (formData) => api.post('/kyc/member-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const retentionAPI = {
  scan:     (gymId) => api.post(`/retention/scan/${gymId}`),
  summary:  (params) => api.get('/retention/summary', { params }),
  getAlerts:(params) => api.get('/retention/alerts', { params }),
  resolve:  (id) => api.patch(`/retention/alerts/${id}/resolve`),
  nudge:    (id) => api.post(`/retention/alerts/${id}/nudge`),
};

export const posAPI = {
  getTransactions: (params) => api.get('/pos', { params }),
  createTransaction: (data) => api.post('/pos', data),
};




export const metaAdsAPI = {
  connectOAuth: (data) => api.post('/meta/oauth/connect', data),
  getConnectedAccount: () => api.get('/meta/oauth/account'),
  disconnectAccount: () => api.delete('/meta/oauth/account'),
  getAccountStatus: (params) => api.get('/meta/account-status', { params }),
  getCampaigns: (params) => api.get('/meta/campaigns', { params }),
  createCampaign: (data) => api.post('/meta/campaigns', data),
  updateCampaign: (campaignId, data) => api.patch(`/meta/campaigns/${campaignId}`, data),
  deleteCampaign: (campaignId, params) => api.delete(`/meta/campaigns/${campaignId}`, { params }),
  getLeadForms: (params) => api.get('/meta/lead-forms', { params }),
  createLeadForm: (data) => api.post('/meta/lead-forms', data),
  getFormLeads: (formId, params) => api.get(`/meta/lead-forms/${formId}/leads`, { params }),
  getAllStoredLeads: () => api.get('/meta/leads'),
  convertLeadToMember: (leadId) => api.post(`/meta/leads/${leadId}/convert`),
};

export const socialMediaAPI = {
  uploadMedia: (file) => uploadAPI.upload(file),
  publishPost: (data) => api.post('/meta/social/publish', data),
  getPosts: () => api.get('/meta/social/posts'),
  syncPostMetrics: (postId) => api.post(`/meta/social/posts/${postId}/sync`),
  deletePost: (postId) => api.delete(`/meta/social/posts/${postId}`),
  getPageInfo: () => api.get('/meta/social/page-info'),
};


export default api;


export const pauseAPI = {
  getPauseSettings:  (gymId)   => api.get(`/gyms/${gymId}/pause-settings`),
  savePauseSettings: (gymId, data) => api.put(`/gyms/${gymId}/pause-settings`, data),
  initiatePause:     (data)    => api.post('/pauses', data),
  cancelPause:       (pauseId) => api.patch(`/pauses/${pauseId}/cancel`),
  getPauseHistory:   (userId)  => api.get(`/pauses/member/${userId}`),
};

export const gymUpiAPI = {
  // UPI settings
  getSettings:    (gymId)        => api.get(`/gym-upi/settings/${gymId}`),
  upsertSettings: (gymId, data)  => api.put(`/gym-upi/settings/${gymId}`, data),
  // Payments
  getStats:       (gymId)        => api.get(`/gym-upi/payments/${gymId}/stats`),
  listPayments:   (gymId, params) => api.get(`/gym-upi/payments/${gymId}`, { params }),
  verifyPayment:  (id)           => api.patch(`/gym-upi/payments/${id}/verify`),
  rejectPayment:  (id, reason)   => api.patch(`/gym-upi/payments/${id}/reject`, { reason }),
};




